// // Add this near the top of main.js
// console.log('Current Environment:', process.env.NODE_ENV)
// console.log('Base URL:', process.env.VUE_APP_BASE_URL)


/**
 * This is the main entry point of the Vue.js application. 
 * It sets up the Vue instance, configures the Vuex store, and mounts the application to the DOM element with the ID 'app'.
 *
 * The application is configured using various props extracted from the DOM element with the ID 'app'.
 * These props include the TPSP name, API version, flags to show or hide certain UI elements, and the form type.
 *
 * The application also uses the 'v-money' library for currency formatting.
 */
import { createApp } from 'vue'
import App from '@/App.vue'
import store from '@/store'





/**
 * Extracts application properties from the DOM element with the ID 'app'.
 * These properties include the TPSP name, API version, flags to show or hide certain UI elements, and the form type.
 *
 * @returns {Object} An object containing the extracted application properties.
 */
const el = document.querySelector('#app')

const parseBooleanAttr = (value, defaultValue) => {
    if (value === null || value === undefined) return defaultValue;
    return value.toLowerCase() === 'true';
}

// Get URL parameters
const urlParams = new URLSearchParams(window.location.search);
const tpsp_internal_id = urlParams.get("tpspid") || '';

// Sanitise URL parameter values: strip HTML tags, trim, enforce max length
const sanitise = (str, maxLength = 200) => {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '').trim().substring(0, maxLength);
};
// Validate platform IDs: only allow lowercase alphanumeric and hyphens
const isValidPlatformId = (id) => /^[a-z0-9-]+$/.test(id);

// Parse URL parameters for form pre-filling
// NOTE: Platform credentials (username/password) are deliberately NOT pre-fillable
// via URL parameters. Credentials must always be entered manually on the form.
const urlPrefill = { userFields: {}, platforms: [] };
const userFieldParams = ['firstname', 'surname', 'telephone', 'email', 'company_name', 'fca_number', 'tpsp_internal_id'];
userFieldParams.forEach(field => {
  const value = sanitise(urlParams.get(field));
  if (value) urlPrefill.userFields[field] = value;
});
// platforms param: comma-separated platform IDs
const platformsParam = urlParams.get('platforms');
if (platformsParam) {
  urlPrefill.platforms = platformsParam.split(',')
    .map(id => id.trim().toLowerCase())
    .filter(id => id && isValidPlatformId(id));
}



const appProps = {
    tpspName: el.getAttribute('data-tpsp-name') || 'Default TPSP Name',
    apiVersion: el.getAttribute('data-api-version') || '2.0',
    showAdditionalInformation: parseBooleanAttr(el.getAttribute('data-show-additional-information'), false),
    showTpspInternalId: parseBooleanAttr(el.getAttribute('data-show-tpsp-internal-id'), false),
    tpspIdLabel: el.getAttribute('data-tpsp-id-label') || '',
    platformRequestEmailAdditionalCc: el.getAttribute('data-platform-request-email-additional-cc') || '',
    finioProductValuations: parseBooleanAttr(el.getAttribute('data-finio-product-valuations'), true),
    finioProductTransactions: parseBooleanAttr(el.getAttribute('data-finio-product-transactions'), false),
    finioProductHistory: parseBooleanAttr(el.getAttribute('data-finio-product-history'), false),
    finioProductIncomeStatements: parseBooleanAttr(el.getAttribute('data-finio-product-income-statements'), false),
    formType: el.getAttribute('data-form-type') || 'tpsp',
    tpspInternalId: tpsp_internal_id,
    redirectUrl: el.getAttribute('data-redirect-url') || null,
    platformGuideLabel: el.getAttribute('data-platform-guide-label') || 'View the Platform Guide',
    urlPrefill,
}

// console.log('App Props:', appProps)



/**
 * Initializes the Vue.js application by:
 * - Creating a new Vue instance using the `App` component
 * - Registering the Vuex store with the Vue instance
 * - Providing the `appProps` object to the Vue instance
 * - Dispatching the `initializeApp` action in the Vuex store, passing the `appProps` object
 * - Mounting the Vue instance to the DOM element with the ID 'app'
 *
 * If there is an error during the initialization process, it logs the error to the console.
 */
const app = createApp(App)
app.use(store)
app.provide('appProps', appProps)

/**
 * Initializes the Vue.js application by dispatching the 'initializeApp' action in the Vuex store, passing the `appProps` object,
 * and then mounting the Vue instance to the DOM element with the ID 'app'. 
 * 
 * If there is an error during the initialization process, it logs the error to the console.
 */
store.dispatch('initializeApp', { appProps }).then(() => {
    app.mount('#app')
}).catch(error => {
    console.error('Failed to initialize app:', error)
})
